import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { FormControl, PageTitle, PageWrapper } from '@ingrid/luterkort-react';

import { MutationSaveBar } from '~src/components/MutationSaveBar/MutationSaveBar';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { Format } from '~src/utils/format';

import { AddressBookForm, FormValues } from '../components/address-book-form';
import { Breadcrumbs } from '../components/breadcrumbs';
import { useAddressBookUpdateMutation } from '../mutations';
import { useAddressBookDetailsQuery } from '../queries';
import { Actions } from './actions';

export function Page() {
  const { addressBookID } = useParams();
  const { data } = useAddressBookDetailsQuery({ id: addressBookID });
  const { t } = useTranslation();
  const { toast } = useToast();

  const form = useForm<FormValues>({ defaultValues: data.contact });
  const mutation = useAddressBookUpdateMutation();

  const viewportRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  async function onSubmit(payload: FormValues) {
    const promise = mutation.mutateAsync({
      ...payload,
      additional_fields: { additional_fields: payload.additional_fields },
      id: payload.id,
    });

    await toast.promise(promise, {
      success: { title: t('addressBook.update.success') },
      error: { title: t('addressBook.update.error') },
      loading: { title: t('addressBook.update.loading') },
    });

    navigate('..');
  }

  return (
    <FormControl.Root>
      <FormProvider {...form}>
        <PageWrapper variant="center" gap="lg" style={{ marginBottom: MutationSaveBar.Offset }}>
          <FormControl.Form onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <PageTitle.Wrapper ref={viewportRef}>
            <Breadcrumbs current={Format.Id(addressBookID ?? '')} />
            <Actions />
          </PageTitle.Wrapper>

          <AddressBookForm />

          <MutationSaveBar viewportRef={viewportRef}>
            <MutationSaveBar.Cancel to=".." />

            <MutationSaveBar.Submit
              onClick={form.handleSubmit(onSubmit)}
              isLoading={mutation.isPending}
              label={t('common.save')}
            />
          </MutationSaveBar>
        </PageWrapper>
      </FormProvider>
    </FormControl.Root>
  );
}
